import * as yup from 'yup';

import { Flags, FlagsActions } from './flags';
import { RequestTypes } from './request-types';
import { Settings, SettingsActions } from './settings';
import { Meta, MetaActions } from './meta';
import { Document, DocumentActions } from './document';
import { FileActions, UserFilesKeyed } from './file';
import { RecipientActions, RecipientKeyed } from './recipient';
import { WorkflowActions, Workflow } from './workflow';
import { CoverPageActions, CoverPage } from './coverPage';
import { IntegrationActions } from './integration';
import { DataSchema, TemplateResponse } from 'hellospa/app-context/actions/prep-and-send';
import { UserActions, User } from './user';
import { ApiAppActions, ApiApp } from './api-app';
import { BulkSendActions, BulkSendData } from './bulk-send';
import { CCActions, CC } from './cc';
import { DeepIntegrationActions } from './deep-integration';
import {
  DeepIntegrationErrorDescriptor,
  FileErrorDescriptor,
} from 'hellospa/components/prep-and-send/file-uploader/messages';
import { EmbeddedData } from './embedded';
import { Rule } from 'hellospa/conditional-logic/types';
import { Page, Field } from 'hellospa/components/editor/types';

export * from './flags';
export * from './settings';
export * from './meta';
export * from './document';
export * from './file';
export * from './recipient';
export * from './cc';
export * from './coverPage';
export * from './user';
export * from './api-app';
export * from './deep-integration';
export * from './workflow';
export * from './integration';
export * from './bulk-send';
export * from './embedded';

export const transmissionGroupGuidSchema = yup.string().trim().required();

export { RequestTypes };

export type Contact = {
  id: string,
  name: string,
  value: string,
  numUses: number,
  type: 'F' | 'E'
}

export type Template = {
  id: string,
  title: string,
  isLocked: boolean,
  isOrdered: boolean,
  dateFormat: string,
  allowReassign: boolean,
  roles: string[],
}

export type EditorFields = {
  pages: Page[],
  fields: Field[],
  rules: Rule[],
}

export type PrepAndSendNamespaceState = {
  // Contacts/referAFriend should probably be moved out of the P&S namespace.
  // It's not exclusive to P&S because the import contacts modal is accessible
  // from the user's profile. Right now it doesn't seem worth the effort of
  // creating a new namespace because it's not simple and changes a bunch of
  // files. When I add @react-js/toolkit I intend to make it easier to make new
  // namespaces.
  readonly referAFriendURL?: string,
  readonly contacts: {
    readonly lastUpdated: number,
    // The contacts are sorted by numUses
    readonly sortedData: Contact[],
  }
  readonly transmissionGroupGuid: string,
  readonly document: Document,
  readonly templates: TemplateResponse[],
  // The Partials here acknowledge that not every ID will exist
  readonly files: UserFilesKeyed,
  readonly editorFields: EditorFields,
  readonly coverPage: Partial<CoverPage>,
  readonly recipients: Array<RecipientKeyed>,
  readonly ccs: Array<CC>,
  readonly user: User,
  readonly apiApp: undefined | ApiApp,
  readonly flags: Flags,
  readonly settings: Settings,
  readonly meta: Meta,
  readonly workflow: Workflow,
  readonly bulkSendData: BulkSendData,
  readonly integrationError?: FileErrorDescriptor,
  readonly deepIntegrationError?: DeepIntegrationErrorDescriptor,
  readonly embeddedData?: EmbeddedData,
  readonly numFields: number,
  readonly nextClickTracker: number,
  readonly requestIsFinished: boolean,
}

export enum Actions {
  SetReferAFriendURL = 'prep-and-send/SetReferAFriendURL',
  UpdateContacts = 'prep-and-send/UpdateContacts',
  SetValidationErrors = 'prep-and-send/SetValidationErrors',
  Init = 'prep-and-send/Init',
  InitPreviewData = 'prep-and-send/InitPreviewData',
  SetFlags = 'prep-and-send/SetFlags',
  UpdateSettings = 'prep-and-send/UpdateSettings',
  UpdateMeta = 'prep-and-send/UpdateMeta',
  UpdateUser = 'prep-and-send/UpdateUser',
  UpdateApiApp = 'prep-and-send/UpdateApiApp',
  SetWorkflow = 'prep-and-send/SetWorkflow',
  CreateRecipient = 'prep-and-send/CreateRecipient',
  UpdateRecipient = 'prep-and-send/UpdateRecipient',
  UpdateCC = 'prep-and-send/UpdateCC',
  CreateCC = 'prep-and-send/CreateCC',
  SetCCs = 'prep-and-send/SetCCs',
  DeleteCC = 'prep-and-send/DeleteCC',
  DeleteRecipient = 'prep-and-send/DeleteRecipient',
  ResetRecipients = 'prep-and-send/ResetRecipients',
  CreateAttachment = 'prep-and-send/CreateAttachment',
  UpdateAttachment = 'prep-and-send/UpdateAttachment',
  DeleteAttachment = 'prep-and-send/DeleteAttachment',
  UpdateDocument = 'prep-and-send/UpdateDocument',
  CreateFile = 'prep-and-send/CreateFile',
  ConvertFile = 'prep-and-send/ConvertFile',
  UpdateFile = 'prep-and-send/UpdateFile',
  DeleteFile = 'prep-and-send/DeleteFile',
  UploadFile = 'prep-and-send/UploadFile',
  UpdateField = 'prep-and-send/UpdateField',
  UseTemplate = 'prep-and-send/UseTemplate',
  DeleteTemplate = 'prep-and-send/DeleteTemplate',
  ExternalFile = 'prep-and-send/ExternalFile',
  UpdateIntegrationStatus = 'prep-and-send/UpdateIntegrationStatus',
  PollFile = 'prep-and-send/PollFile',
  ReorderFile = 'prep-and-send/ReorderFile',
  UpdateCoverPage = 'prep-and-send/UpdateCoverPage',
  DeleteCoverPage = 'prep-and-send/DeleteCoverPage',
  CreateCoverPage = 'prep-and-send/CreateCoverPage',
  SynchronizeStore='prep-and-send/SynchronizeStore',
  SetBulkSendData='prep-and-send/SetBulkSendData',
  SetIntegrationError='prep-and-send/SetIntegrationError',
  SetDeepIntegrationError='prep-and-send/SetDeepIntegrationError',
  SetIntegrationExternalFilename='prep-and-send/SetIntegrationExternalFilename',
  SetIntegrationDefaultSavePath ='prep-and-send/SetIntegrationDefaultSavePath',
  UpdateNumberFields='prep-and-send/UpdateNumberFields',
  // TODO: clean up when DEV-12786 is done
  UpdateNextClickTracker='prep-and-send/UpdateNextClickTracker',
  UpdateRequestIsFinished='prep-and-send/UpdateRequestIsFinished',
  UpdateShouldConvertDocToTemplate='prep-and-send/UpdateShouldConvertDocToTemplate'
}

export enum DropzoneDisplayTypes {
  MenuLayout = 'menu',
  InlineLayout = 'inline',
}

export interface InitAction {
  type: Actions.Init,
  payload: DataSchema,
}

export interface InitPreviewDataAction {
  type: Actions.InitPreviewData,
  payload: DataSchema['data']['editorFields'],
}

export interface SetReferAFriendURLAction {
  type: Actions.SetReferAFriendURL,
  payload: string,
}
export interface UpdateContactsAction {
  type: Actions.UpdateContacts,
  payload: {
    lastUpdated: number,
    sortedData: Contact[]
  }
}
export interface UpdateNumberFieldsAction {
  type: Actions.UpdateNumberFields,
  payload: number,
}

// TODO: clean up when DEV-12786 is done
export interface UpdateNextClickTracker {
  type: Actions.UpdateNextClickTracker,
  payload: number,
}
export interface UpdateRequestIsFinished {
  type: Actions.UpdateRequestIsFinished,
  payload: boolean,
}

export interface UpdateShouldConvertDocToTemplate {
  type: Actions.UpdateShouldConvertDocToTemplate,
  payload: boolean,
}

export type PrepAndSendAction =
  | InitAction
  | InitPreviewDataAction
  | RecipientActions
  | CCActions
  | FlagsActions
  | SettingsActions
  | MetaActions
  | UserActions
  | ApiAppActions
  | DocumentActions
  | FileActions
  | UpdateContactsAction
  | SetReferAFriendURLAction
  | CoverPageActions
  | WorkflowActions
  | IntegrationActions
  | BulkSendActions
  | DeepIntegrationActions
  | UpdateNumberFieldsAction
  // TODO: clean up when DEV-12786 is done
  | UpdateNextClickTracker
  | UpdateRequestIsFinished
  | UpdateShouldConvertDocToTemplate;

export type ProProUpsellInfo = {
    showBanner: boolean,
    showModal: boolean,
    variation: string,
    goalType: string,
    coolDownKey: string,
    bannerRedirectUrl: string,
    modalRedirectUrl: string,
    isEligible: boolean,
    planName: string,
  };
